import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Zap, KeyRound, DoorClosed, Home, Store, Building2, Warehouse, HelpCircle, Phone, CheckCircle2 } from "lucide-react";
import { PHONE_DISPLAY, PHONE_TEL } from "@/lib/site";
import Reveal, { SectionHead, EASE } from "@/components/Reveal";

const TYPES = [
  { id: "elettrico", label: "Guasto elettrico", note: "Blackout, luci, prese, salvavita", icon: Zap },
  { id: "serratura", label: "Porta bloccata", note: "Chiavi dentro o spezzate", icon: KeyRound },
  { id: "cancello", label: "Cancello elettrico", note: "Cancello fermo, motore in errore", icon: DoorClosed },
];

const PLACES = [
  { id: "Casa", icon: Home },
  { id: "Negozio", icon: Store },
  { id: "Ufficio", icon: Building2 },
  { id: "Condominio", icon: Warehouse },
  { id: "Altro", icon: HelpCircle },
];

export default function EmergencySelector() {
  const [type, setType] = useState(null);
  const [place, setPlace] = useState(null);

  const selectedType = TYPES.find((t) => t.id === type);

  return (
    <section id="emergenza" className="relative overflow-hidden py-20 lg:py-28">
      <div
        aria-hidden="true"
        className="absolute left-1/2 top-0 h-[400px] w-[700px] -translate-x-1/2 rounded-full bg-volt/5 blur-[130px]"
      />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6">
        <SectionHead
          kicker="2 click, zero attese"
          title="Seleziona la tua emergenza e parti subito"
          sub="Rispondi in due tocchi: prepariamo la richiesta e il tecnico più vicino riceve tutto in anticipo."
        />

        <div className="mt-12">
          <div>
            <Reveal>
            <div className="mt-4 flex items-center gap-2.5">
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-deep font-display text-sm font-extrabold text-volt">
                1
              </span>
              <p className="font-display text-lg font-bold text-volt sm:text-xl">
                Cosa è successo?
              </p>
            </div>
            </Reveal>
            <div className="mt-5 grid gap-3.5 sm:grid-cols-3">
              {TYPES.map(({ id, label, note, icon: Icon }, i) => {
                const active = type === id;
                return (
                  <Reveal key={id} delay={0.06 * i}>
                    <button
                      type="button"
                      data-testid={`emergency-calculator-select-${id}`}
                      onClick={() => setType(id)}
                      aria-pressed={active}
                      className={`group h-full w-full rounded-2xl border p-5 text-left transition-all duration-300 ${
                        active
                          ? "border-volt bg-volt/10 shadow-[0_0_32px_rgba(255,195,0,0.18)]"
                          : "border-line bg-card hover:-translate-y-1 hover:border-volt/40"
                      }`}
                    >
                      <Icon
                        size={26}
                        strokeWidth={2.1}
                        className={active ? "text-volt" : "text-slate-400 transition-colors group-hover:text-volt"}
                      />
                      <span className="mt-3 block font-display text-base font-bold text-white">
                        {label}
                      </span>
                      <span className="mt-1 block text-xs leading-relaxed text-black">
                        {note}
                      </span>
                    </button>
                  </Reveal>
                );
              })}
            </div>

            <AnimatePresence>
              {type && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.5, ease: EASE }}
                  className="overflow-hidden"
                >
                  <div className="mt-9 flex items-center gap-2.5">
                    <span className="flex h-7 w-7 items-center justify-center rounded-full bg-deep font-display text-sm font-extrabold text-volt">
                      2
                    </span>
                    <p className="font-display text-lg font-bold text-volt sm:text-xl">
                      Dove ti trovi?
                    </p>
                  </div>
                  <div className="mt-5 flex flex-wrap gap-2.5">
                    {PLACES.map(({ id, icon: Icon }) => {
                      const active = place === id;
                      return (
                        <button
                          key={id}
                          type="button"
                          data-testid={`emergency-calculator-select-${id.toLowerCase()}`}
                          onClick={() => setPlace(id)}
                          aria-pressed={active}
                          className={`flex items-center gap-2 rounded-full border px-4.5 py-2.5 text-sm font-medium transition-all duration-300 ${
                            active
                              ? "border-volt bg-volt text-night"
                              : "border-line bg-card text-slate-300 hover:border-volt/40 hover:text-white"
                          }`}
                          style={{ paddingLeft: 18, paddingRight: 18 }}
                        >
                          <Icon size={15} strokeWidth={2.2} />
                          {id}
                        </button>
                      );
                    })}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <Reveal delay={0.1} className="mt-10">
            <div
              className={`relative overflow-hidden rounded-3xl border p-7 transition-colors duration-500 sm:p-9 ${
                type ? "border-volt/50 bg-ink" : "border-line bg-card"
              }`}
            >
              <div
                aria-hidden="true"
                className={`absolute -right-20 -top-20 h-56 w-56 rounded-full bg-volt/15 blur-[80px] transition-opacity duration-700 ${
                  type ? "opacity-100" : "opacity-0"
                }`}
              />
              <AnimatePresence mode="wait">
                {!type ? (
                  <motion.div
                    key="idle"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.4 }}
                  >
                    <p className="mt-4 whitespace-nowrap text-center font-display text-[clamp(0.4rem,2.02vw,1.9rem)] font-extrabold uppercase animate-blink-soft text-red-500">
                      Scegli il tipo di emergenza per attivare il tecnico più vicino a te.
                    </p>
                  </motion.div>
                ) : (
                  <motion.div
                    key="ready"
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.5, ease: EASE }}
                  >
                    <p className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-emerald-400">
                      <CheckCircle2 size={15} /> Richiesta pronta
                    </p>
                    <p className="mt-4 font-display text-xl font-bold text-white sm:text-2xl">
                      {selectedType.label}
                      {place && <span className="text-volt"> · {place}</span>}
                    </p>
                    <p className="mt-2 text-sm text-slate-400">
                      Roma e Provincia · allerta inviata al tecnico più vicino
                    </p>

                    <a
                      href={PHONE_TEL}
                      data-testid="emergency-calculator-submit"
                      className="mt-7 flex h-14 w-full items-center justify-center gap-3 rounded-full bg-volt font-display text-base font-extrabold text-night shadow-[0_0_30px_rgba(255,195,0,0.35)] transition-all duration-300 hover:bg-volthi hover:shadow-[0_0_44px_rgba(255,195,0,0.55)] active:scale-95"
                    >
                      <Phone size={19} strokeWidth={2.5} />
                      CHIAMA · {PHONE_DISPLAY}
                    </a>
                    <p className="mt-4 text-center font-mono text-[11px] uppercase tracking-[0.18em] text-slate-500">
                      Risposta immediata · 24 ore su 24
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
